import { type ActorView, getProfile, searchActors } from "../../queries.ts";
import { cacheActor } from "../view-cache.ts";
import { publicTransport, RpcClient } from "../api.ts";
import { parseRoute, postPathFromUri, profilePath, type Router } from "../router.ts";
import { Avatar } from "./actor.tsx";

export type SearchTarget =
  | { readonly kind: "route"; readonly path: string; readonly label: string }
  | { readonly kind: "external"; readonly href: string; readonly label: string };

export function SearchPanel(router: Router): Element {
  const appView = import("../config.ts").then(({ config }) =>
    new RpcClient(publicTransport(config.appView))
  );
  const input = (
    <input
      type="search"
      name="q"
      maxLength={256}
      autocomplete="off"
      placeholder="Search"
      aria-label="Find a profile or post"
    />
  ) as HTMLInputElement;
  const results = <div class="search-results" aria-live="polite"></div>;
  let actors: readonly ActorView[] = [];
  // Full profiles for avatars; search results carry none.
  const profiles = new Map<string, ActorView>();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let request: AbortController | undefined;

  const render = (target: SearchTarget | undefined, message?: string) => {
    results.replaceChildren();
    if (target) results.append(targetView(target));
    for (const actor of actors) {
      const view = profiles.get(actor.did) ?? actor;
      cacheActor(view);
      const name = view.displayName?.trim();
      const label = name || view.handle || view.did;
      results.append(
        <a
          class="search-result"
          href={profilePath(actor.handle ?? actor.did)}
          dataset={{ route: "" }}
        >
          {Avatar(view, "avatar search-avatar")}
          <span>
            <strong>{label}</strong>
            {view.handle && <small>@{view.handle}</small>}
          </span>
        </a>,
      );
    }
    if (message && !target && actors.length === 0) {
      results.append(<small class="search-message">{message}</small>);
    }
  };

  // Search results carry no avatars; fill them from cached profiles.
  const resolveAvatars = (snapshot: readonly ActorView[]): void => {
    const missing = snapshot.filter(
      (actor) => actor.avatar === undefined && !profiles.has(actor.did),
    );
    if (!missing.length) return;
    void appView.then((client) =>
      Promise.all(
        missing.map((actor) =>
          client.call(getProfile, { parameters: { actor: actor.did } }).then(
            (value) => profiles.set(actor.did, value.actor),
            () => undefined,
          )
        ),
      )
    ).then(() => {
      if (actors === snapshot) render(searchTarget(input.value.trim(), location.href));
    });
  };

  const update = () => {
    clearTimeout(timer);
    request?.abort();
    request = undefined;
    actors = [];
    const value = input.value.trim();
    const target = searchTarget(value, location.href);
    if (!value) {
      render(undefined);
      return;
    }
    render(target);
    if (value.length < 2 || value.includes("/")) return;

    timer = setTimeout(() => {
      const controller = new AbortController();
      request = controller;
      void appView.then((client) =>
        client.call(searchActors, {
          parameters: { q: value.replace(/^@/, ""), limit: 6 },
          signal: controller.signal,
        })
      ).then((page) => {
        if (request !== controller) return;
        actors = page.actors;
        resolveAvatars(actors);
        render(searchTarget(input.value.trim(), location.href));
      }).catch((error) => {
        if (
          request !== controller || error instanceof DOMException && error.name === "AbortError"
        ) {
          return;
        }
        actors = [];
        render(searchTarget(input.value.trim(), location.href), "Suggestions unavailable");
      });
    }, 180);
  };

  input.addEventListener("input", update);
  return (
    <section class="search-panel" aria-label="Search">
      <form
        class="search-form"
        _onsubmit={(event) => {
          event.preventDefault();
          const target = searchTarget(input.value.trim(), location.href);
          if (target?.kind === "route") router.navigate(target.path);
          else if (target) location.assign(target.href);
          else if (actors[0]) router.navigate(profilePath(actors[0].handle ?? actors[0].did));
          else render(undefined, "Enter a handle, DID, or post URL");
        }}
      >
        {input}
        <button type="submit">Go</button>
      </form>
      {results}
    </section>
  );
}

export function searchTarget(value: string, base: string): SearchTarget | undefined {
  const query = value.trim().replace(/^@/, "");
  if (!query) return undefined;

  const ceruleaPost = postPathFromUri(query);
  if (ceruleaPost) return { kind: "route", path: ceruleaPost, label: "Open Cerulea post" };

  const bskyPost = /^at:\/\/(did:[^/]+)\/app\.bsky\.feed\.post\/([^/]+)$/.exec(query);
  if (bskyPost) {
    return {
      kind: "external",
      href: bskyPostURL(bskyPost[1]!, bskyPost[2]!),
      label: "Open Bluesky post",
    };
  }

  try {
    const url = new URL(query, base);
    if (url.hostname === "bsky.app") {
      const match = /^\/profile\/([^/]+)(?:\/post\/([^/]+))?\/?$/.exec(url.pathname);
      if (match?.[2]) {
        return {
          kind: "external",
          href: bskyPostURL(decodeURIComponent(match[1]!), decodeURIComponent(match[2])),
          label: "Open Bluesky post",
        };
      }
      if (match?.[1]) {
        return {
          kind: "route",
          path: profilePath(decodeURIComponent(match[1])),
          label: "Open profile",
        };
      }
    }
    const route = parseRoute(url.pathname);
    if (route.name === "profile" || route.name === "post") {
      return { kind: "route", path: url.pathname, label: `Open ${route.name}` };
    }
  } catch {
    // Not a URL; it may still be an actor identifier.
  }

  if (/^did:[^/\s]+$/.test(query) || /^(?:[a-z0-9-]+\.)+[a-z][a-z0-9-]*$/i.test(query)) {
    return { kind: "route", path: profilePath(query), label: "Open profile" };
  }
  return undefined;
}

function targetView(target: SearchTarget): Element {
  return target.kind === "route"
    ? (
      <a class="search-result search-direct" href={target.path} dataset={{ route: "" }}>
        {target.label}
      </a>
    )
    : <a class="search-result search-direct" href={target.href}>{target.label} ↗</a>;
}

function bskyPostURL(actor: string, rkey: string): string {
  return `https://bsky.app/profile/${actor}/post/${rkey}`;
}
